import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { useAlert } from "../../../context/AlertContext";
import { formatDate } from "../../../commons/FormatValue";
import { useLoading } from "../../../context/LoadingContext";
import { filteredItemBySearch } from "../../../utils/ManageArray";
import { getServiceRequest } from "../../../commons/ServiceRequest";
import Table from "../../Table";
import ConfirmModal from "../../ConfirmModal";
import SearchFilter from "../../SearchFilter";
import CardDisplayComponent from "../../CardDisplayComponent";
import CustomersService from "../../../services/customer.service";
import CustContractsService from "../../../services/custcontract.service";

const BindContractBranchForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showAlert } = useAlert();
  const { loading, setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [data, setData] = useState([]);
  const [branch, setBranch] = useState([]);
  const [contract, setContract] = useState([]);
  const defaultSearch = [
    {
      name: "Contract_No",
      label: "เลขที่สัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: editData ? "text" : "select",
      size: 4,
      disabled: !!editData,
      queryService: (query) =>
        getServiceRequest({
          showAlert,
          setLoading,
          serviceRequest: CustContractsService.getAllCustContracts(query),
          callback: ({ items }) =>
            setContract(
              items.filter(({ Customer }) =>
                Customer?.Cus_Code.endsWith("0000")
              )
            )
        })
    },
    {
      name: "Contract_Start_Date",
      label: "วันที่เริ่มสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      size: 4,
      disabled: true,
      findValue: { value: "Contract_No" }
    },
    {
      name: "Contract_End_Date",
      label: "วันที่สิ้นสุดสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      size: 4,
      disabled: true,
      findValue: { value: "Contract_No" }
    },
    {
      name: "Customer.Cus_Code",
      label: "รหัสลูกค้า/สาขา",
      type: "text",
      size: 4,
      disabled: true,
      findValue: { value: "Contract_No" }
    },
    {
      name: "Customer.Cus_NumBranch",
      label: "รหัสลูกค้า/สาขา",
      type: "text",
      size: 2,
      disabled: true,
      findValue: { value: "Contract_No" }
    },
    {
      name: "Customer.Cus_Name",
      label: "ชื่อลูกค้า/สาขา",
      type: "text",
      size: 6,
      disabled: true,
      findValue: { value: "Contract_No" }
    }
  ];
  const [searchInput, setSearchInput] = useState(defaultSearch);
  const columns = [
    {
      name: "ลำดับ",
      selector: (row) => row.rowIndex,
      sortable: true,
      width: "110px"
    },
    {
      name: "รหัสสาขา",
      selector: (row) => row.Customer?.Cus_Code,
      minWidth: "180px"
    },
    {
      name: "เลขที่สาขา",
      selector: (row) => row.Customer?.Cus_NumBranch,
      minWidth: "180px"
    },
    {
      name: "ชื่อสาขา",
      selector: (row) => row.Customer?.Cus_Name,
      minWidth: "300px"
    },
    {
      name: "เลขที่โครงการ",
      selector: (row) => row.CustContract?.Project_No,
      minWidth: "180px"
    },
    {
      name: "เลขที่สัญญา",
      selector: (row) => row.Contract_No,
      minWidth: "180px"
    },
    {
      name: "วันที่เริ่มต้นสัญญา",
      selector: (row) => row.CustContract?.Contract_Start_Date,
      format: (row) => formatDate(row.CustContract?.Contract_Start_Date),
      minWidth: "180px"
    },
    {
      name: "วันที่สิ้นสุดสัญญา",
      selector: (row) => row.CustContract?.Contract_End_Date,
      format: (row) => formatDate(row.CustContract?.Contract_End_Date),
      minWidth: "180px"
    }
  ];

  const handleSearchData = (search) => {
    if (editData) {
      getServiceRequest({
        showAlert,
        setLoading,
        serviceRequest: CustContractsService.getAllCustContracts(
          search?.[0]?.value
        ),
        callback: (res) => setContract(res)
      });
    }
    const { Contract_No, Cus_Id, Customer, Com_Code, ...CustContract } =
      filteredItemBySearch(contract, search)?.[0];

    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: CustomersService.getCustomerBranches(Cus_Id),
      callback: (res) => {
        const updatedBranch = [
          { Cus_Id, Contract_No, CustContract, Customer, Com_Code }
        ];
        res.map(({ Id, Com_Code, ...Customer }) =>
          updatedBranch.push({
            Cus_Id: Id,
            Contract_No,
            CustContract,
            Customer,
            Com_Code
          })
        );
        setBranch(updatedBranch);
      }
    });
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      const modifiedData = {
        Array: dataCallback?.data.map(
          ({ rowIndex, CustContract, Customer, ...Cus_Branch }) => Cus_Branch
        )
      };
      if (!editData) onSubmitCallback(modifiedData);
      else onSubmitCallback(modifiedData.Array[0]);
    }
  };

  useEffect(() => {
    if (editData) {
      setSearchInput(
        defaultSearch.map((search) =>
          search?.name === "Contract_No"
            ? { ...search, value: editData?.Contract_No }
            : search
        )
      );

      getServiceRequest({
        showAlert,
        setLoading,
        serviceRequest: CustContractsService.getCustContracts(
          `?keyword=${editData?.Contract_No}`
        ),
        callback: ({ items }) => {
          setContract(items);

          getServiceRequest({
            showAlert,
            setLoading,
            serviceRequest: CustomersService.getCustomers(editData?.Cus_Id),
            callback: ({ Id, Com_Code, ...res }) =>
              setBranch([
                {
                  Cus_Id: Id,
                  Contract_No: editData?.Contract_No,
                  CustContract: items?.[0],
                  Customer: res,
                  Com_Code
                }
              ])
          });
        }
      });
    }
  }, [editData]);

  return (
    <>
      <CardDisplayComponent
        cardClassName="px-3 px-lg-4 px-xl-8"
        header={{
          title: { text: editData ? "แก้ไขข้อมูลสาขา" : "เพิ่มสาขา" },
          className: "text-center"
        }}
        body={{
          className: "px-3 px-lg-4 px-xl-8",
          element: (
            <SearchFilter
              data={contract}
              inputField={searchInput}
              fieldClassName={{ inputRow: "px-3 px-lg-4 px-xl-8" }}
              searchCallback={handleSearchData}
            />
          )
        }}
      />

      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          header={{ title: { text: "รายละเอียดสาขา" } }}
          body={{
            element: (
              <Table
                data={branch}
                column={columns}
                pending={loading}
                pagination={!editData}
                selectableRows
                // selectableRowSelected={(row) =>
                //   row?.Contract_No === editData?.Contract_No
                // }
                onSelectedRowsChange={({ selectedRows }) =>
                  setData(selectedRows)
                }
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="px-5 py-2 text-dark rounded-pill"
                >
                  {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
                </Button>
                <ConfirmModal
                  show={showModal}
                  data={{ data }}
                  content={editData ? "edit" : "save"}
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-success"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() => navigate("/PM_work/bind", { state })}
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

BindContractBranchForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default BindContractBranchForm;
